import React, { useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Line,
  LineChart,
  Pie,
  PieChart,
  RadialBar,
  RadialBarChart,
  ResponsiveContainer,
  Sector,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { HelpTooltip } from "./UI";

const COLORS = {
  indigo: "#6366f1",
  emerald: "#10b981",
  amber: "#f59e0b",
  slate: "#94a3b8",
  rose: "#f43f5e",
  violet: "#8b5cf6",
  teal: "#14b8a6",
  blue: "#3b82f6",
};

const PIE_PALETTE = [COLORS.emerald, COLORS.amber, COLORS.slate, COLORS.indigo, COLORS.rose, COLORS.violet];

const AXIS_LABEL_STYLE = { fontSize: 11, fill: "#1e293b", fontWeight: 600 };
const TICK_STYLE = { fontSize: 11, fill: "#334155", fontWeight: 500 };
const VALUE_LABEL_STYLE = { fontSize: 10, fill: "#334155", fontWeight: 600 };

const LEGEND_ITEMS = {
  gapStack: [
    { name: "Critical", color: COLORS.rose },
    { name: "High Priority", color: COLORS.amber },
    { name: "Other", color: COLORS.slate },
  ],
  evidenceStack: [
    { name: "Uploaded", color: COLORS.emerald },
    { name: "Missing", color: COLORS.amber },
  ],
  complianceTrend: [
    { name: "Avg Score", color: COLORS.indigo },
    { name: "Completion", color: COLORS.teal },
  ],
  gapTrend: [
    { name: "Total Gaps", color: COLORS.rose },
    { name: "Depts Submitted", color: COLORS.indigo },
  ],
  yearComparison: [
    { name: "Avg Score (%)", color: COLORS.indigo },
    { name: "Completion (%)", color: COLORS.teal },
    { name: "Total Gaps", color: COLORS.rose },
  ],
};

/** Outside axis titles — avoids overlapping tick values. */
function xAxisLabel(text, extraOffset = 0) {
  return {
    value: text,
    position: "bottom",
    offset: 14 + extraOffset,
    style: AXIS_LABEL_STYLE,
  };
}

function yAxisLabel(text) {
  return {
    value: text,
    angle: -90,
    position: "left",
    offset: 12,
    style: { ...AXIS_LABEL_STYLE, textAnchor: "middle" },
  };
}

function yAxisLabelRight(text) {
  return {
    value: text,
    angle: 90,
    position: "right",
    offset: 12,
    style: { ...AXIS_LABEL_STYLE, textAnchor: "middle" },
  };
}

function chartMargin({ angledX = false, dualY = false, top = 28, extraBottom = 0 } = {}) {
  return {
    top,
    right: dualY ? 84 : 24,
    left: 68,
    bottom: (angledX ? 88 : 48) + extraBottom,
  };
}

function abbreviateLabel(value, max = 16) {
  const s = String(value ?? "").trim();
  if (s.length <= max) return s;
  const slice = s.slice(0, max);
  const lastSpace = slice.lastIndexOf(" ");
  if (lastSpace > max * 0.45) return `${s.slice(0, lastSpace)}…`;
  return `${slice}…`;
}

function makeCategoryTick(data, { angled = false, nameKey = "fullName", displayKey = "name" } = {}) {
  return function CategoryTick({ x, y, payload, index }) {
    const row = data[index] ?? {};
    const fullName = row[nameKey] ?? payload?.value ?? "";
    const display = row[displayKey] ?? payload?.value ?? "";
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={angled ? 8 : 14}
          textAnchor={angled ? "end" : "middle"}
          fill="#334155"
          fontSize={10}
          fontWeight={500}
          transform={angled ? "rotate(-32)" : undefined}
        >
          <title>{fullName}</title>
          {display}
        </text>
      </g>
    );
  };
}

function makeVerticalCategoryTick(data, { nameKey = "fullTitle", displayKey = "name" } = {}) {
  return function VerticalCategoryTick({ x, y, payload, index }) {
    const row = data[index] ?? {};
    const fullName = row[nameKey] ?? payload?.value ?? "";
    const display = row[displayKey] ?? payload?.value ?? "";
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={-6} y={0} dy={4} textAnchor="end" fill="#334155" fontSize={10} fontWeight={500}>
          <title>{fullName}</title>
          {display}
        </text>
      </g>
    );
  };
}

function ChartLegend({ items }) {
  if (!items?.length) return null;
  return (
    <ul className="flex flex-wrap justify-center gap-x-5 gap-y-1.5 pt-2 pb-0.5 m-0 list-none border-t border-slate-100 mt-1">
      {items.map(({ name, color }) => (
        <li key={name} className="inline-flex items-center gap-1.5 text-[11px] text-slate-700">
          <span className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: color }} />
          <span className="font-medium">{name}</span>
        </li>
      ))}
    </ul>
  );
}

function LabeledChart({ height, children, legend }) {
  return (
    <div className="w-full min-w-0 overflow-visible">
      <ResponsiveContainer width="100%" height={height}>
        {children}
      </ResponsiveContainer>
      {legend}
    </div>
  );
}

function departmentChartHeight(count, base = 300) {
  return Math.max(base, base + Math.max(0, count - 5) * 10);
}

function barValueLabel(props) {
  const { x, y, width, value, formatter } = props;
  if (value == null || Number.isNaN(Number(value))) return null;
  const text = formatter ? formatter(value) : value;
  return (
    <text
      x={x + width / 2}
      y={y - 6}
      fill="#334155"
      textAnchor="middle"
      fontSize={10}
      fontWeight={600}
    >
      {text}
    </text>
  );
}

function percentBarLabel(value) {
  if (value == null || Number.isNaN(Number(value))) return "";
  return `${value}%`;
}

function percentTick(value) {
  if (value == null || Number.isNaN(Number(value))) return "";
  return `${value}%`;
}

function signedLabel(value, suffix = "") {
  if (value == null || Number.isNaN(Number(value))) return "";
  const n = Number(value);
  return `${n > 0 ? "+" : ""}${n}${suffix}`;
}

function truncateLabel(value, max = 14) {
  return abbreviateLabel(value, max);
}

function ChartTooltip({ active, payload, label, unit = "" }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-lg text-xs">
      {label && <p className="font-semibold text-slate-800 mb-1">{label}</p>}
      {payload.map((p) => (
        <p key={p.dataKey || p.name} className="text-slate-600">
          <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: p.color || p.fill }} />
          {p.name}: <span className="font-semibold text-slate-900">{p.value}{unit}</span>
        </p>
      ))}
    </div>
  );
}

function ChartCard({ title, tooltip, children, className = "" }) {
  return (
    <div className={`rounded-xl border border-slate-200 bg-white p-4 shadow-sm overflow-visible ${className}`}>
      <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
        {title}
        {tooltip && <HelpTooltip position="bottom" content={tooltip} />}
      </h4>
      {children}
    </div>
  );
}

function ChartEmpty({ message = "No data yet" }) {
  return (
    <div className="flex items-center justify-center h-[240px] text-sm text-slate-400">
      {message}
    </div>
  );
}

/** Donut layout — balanced ring, outside slice labels, legend below. */
const DONUT_CHART_HEIGHT = 280;
const DONUT_SLICE_MARGIN = { top: 28, right: 84, left: 84, bottom: 12 };
const DONUT_INNER = 54;
const DONUT_OUTER = 70;
const RADIAN = Math.PI / 180;

const DONUT_STATUS_TIPS = {
  Completed: "Departments with a locked (finalised) assessment submission.",
  "In Progress": "Departments that started an assessment but have not submitted yet.",
  "Not Started": "Departments with no assessment activity in this cycle.",
  Locked: "Finalised submissions — read-only unless reopened by an admin.",
  Submitted: "Assessments formally submitted by users.",
  Draft: "Assessments saved as draft — not yet submitted.",
  Reopened: "Previously locked assessments unlocked for editing again.",
};

function renderDonutSliceLabel({
  cx,
  cy,
  midAngle,
  outerRadius,
  name,
  percent,
  value,
  labelTotal,
}) {
  const pctNum = labelTotal
    ? Math.round((value / labelTotal) * 100)
    : Math.round(percent * 100);
  if (pctNum < 3) return null;
  const radius = outerRadius + 20;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  const anchor = x > cx ? "start" : "end";

  return (
    <text
      x={x}
      y={y}
      fill="#1e293b"
      textAnchor={anchor}
      dominantBaseline="central"
      fontSize={11}
      fontWeight={600}
    >
      <title>{`${name}: ${value} (${pctNum}%)`}</title>
      <tspan x={x} dy="-0.55em">{name}</tspan>
      <tspan x={x} dy="1.2em" fill="#475569" fontSize={10} fontWeight={600}>
        {pctNum}%
      </tspan>
    </text>
  );
}

function renderDonutActiveShape(props) {
  const {
    cx,
    cy,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
  } = props;
  return (
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius}
      outerRadius={outerRadius + 4}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
      stroke="#fff"
      strokeWidth={2}
    />
  );
}

function DonutTooltip({ active, payload, total, unit = "items" }) {
  if (!active || !payload?.length) return null;
  const entry = payload[0];
  const name = entry.name ?? "";
  const value = entry.value ?? 0;
  const pct = total ? Math.round((value / total) * 100) : 0;
  const tip = DONUT_STATUS_TIPS[name] || "";
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 shadow-lg text-xs max-w-[240px]">
      <p className="font-semibold text-slate-900">{name}</p>
      <p className="text-slate-700 mt-1">
        <span className="font-bold text-base">{value}</span>{" "}
        <span className="text-slate-600">{unit}</span>
        <span className="text-slate-500"> · {pct}% of total</span>
      </p>
      {tip && <p className="text-slate-500 mt-1.5 leading-snug">{tip}</p>}
    </div>
  );
}

function DonutLegend({ items, total, showValue = false, tips = DONUT_STATUS_TIPS }) {
  if (!items?.length) return null;
  return (
    <ul className="flex flex-wrap justify-center gap-x-5 gap-y-2 pt-3 m-0 p-0 list-none w-full border-t border-slate-100 mt-2">
      {items.map((entry) => {
        const value = entry.value ?? 0;
        const name = entry.name ?? "";
        const pct = total ? Math.round((value / total) * 100) : 0;
        const detail = showValue ? `${value} (${pct}%)` : `${pct}%`;
        return (
          <li
            key={name}
            title={tips[name] || name}
            className="inline-flex items-center gap-1.5 text-[11px] text-slate-700 cursor-help"
          >
            <span
              className="inline-block w-3 h-3 rounded-sm flex-shrink-0 ring-1 ring-slate-200/80"
              style={{ background: entry.fill }}
            />
            <span className="font-semibold text-slate-800">{name}</span>
            <span className="text-slate-600">{detail}</span>
          </li>
        );
      })}
    </ul>
  );
}

function DonutChartShell({ chart, legend }) {
  return (
    <div className="w-full overflow-visible">
      <ResponsiveContainer width="100%" height={DONUT_CHART_HEIGHT}>
        {chart}
      </ResponsiveContainer>
      {legend}
    </div>
  );
}

export function DepartmentStatusDonut({ departments }) {
  const [activeIndex, setActiveIndex] = useState(undefined);

  const data = useMemo(() => [
    { name: "Completed", value: departments?.completed ?? 0, fill: COLORS.emerald },
    { name: "In Progress", value: departments?.in_progress ?? 0, fill: COLORS.amber },
    { name: "Not Started", value: departments?.not_started ?? 0, fill: COLORS.slate },
  ].filter((d) => d.value > 0), [departments]);

  const total = departments?.total ?? 0;
  if (!total) return <ChartEmpty message="Add departments to see status breakdown" />;

  const chartData = data.length ? data : [{ name: "None", value: 1, fill: COLORS.slate }];

  return (
    <DonutChartShell
      legend={<DonutLegend items={chartData} total={total} />}
      chart={
        <PieChart margin={DONUT_SLICE_MARGIN} style={{ overflow: "visible" }}>
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={DONUT_INNER}
            outerRadius={DONUT_OUTER}
            paddingAngle={2}
            stroke="#fff"
            strokeWidth={2}
            activeIndex={activeIndex}
            activeShape={renderDonutActiveShape}
            onMouseEnter={(_, index) => setActiveIndex(index)}
            onMouseLeave={() => setActiveIndex(undefined)}
            label={(props) => renderDonutSliceLabel({ ...props, labelTotal: total })}
            labelLine={{ stroke: "#94a3b8", strokeWidth: 1.5 }}
          >
            {chartData.map((entry, i) => (
              <Cell key={i} fill={entry.fill} stroke="#fff" strokeWidth={2} />
            ))}
          </Pie>
          <Tooltip content={<DonutTooltip total={total} unit="departments" />} />
          <text
            x="50%"
            y="50%"
            textAnchor="middle"
            dominantBaseline="central"
            fill="#0f172a"
            fontSize={20}
            fontWeight={700}
          >
            {total}
          </text>
          <text
            x="50%"
            y="50%"
            dy={18}
            textAnchor="middle"
            dominantBaseline="central"
            fill="#475569"
            fontSize={11}
            fontWeight={600}
          >
            departments
          </text>
        </PieChart>
      }
    />
  );
}

export function AssessmentStatusDonut({ assessments }) {
  const [activeIndex, setActiveIndex] = useState(undefined);

  const data = useMemo(() => [
    { name: "Locked", value: assessments?.locked_assessments ?? 0, fill: COLORS.indigo },
    { name: "Submitted", value: assessments?.submitted_assessments ?? 0, fill: COLORS.blue },
    { name: "Draft", value: assessments?.draft_assessments ?? 0, fill: COLORS.amber },
    { name: "Reopened", value: assessments?.reopened_assessments ?? 0, fill: COLORS.rose },
  ].filter((d) => d.value > 0), [assessments]);

  if (!data.length) return <ChartEmpty message="No assessments in this cycle yet" />;

  const total = data.reduce((sum, d) => sum + d.value, 0);

  return (
    <DonutChartShell
      legend={<DonutLegend items={data} total={total} showValue />}
      chart={
        <PieChart margin={DONUT_SLICE_MARGIN} style={{ overflow: "visible" }}>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={DONUT_INNER}
            outerRadius={DONUT_OUTER}
            paddingAngle={2}
            stroke="#fff"
            strokeWidth={2}
            activeIndex={activeIndex}
            activeShape={renderDonutActiveShape}
            onMouseEnter={(_, index) => setActiveIndex(index)}
            onMouseLeave={() => setActiveIndex(undefined)}
            label={(props) => renderDonutSliceLabel({ ...props, labelTotal: total })}
            labelLine={{ stroke: "#94a3b8", strokeWidth: 1.5 }}
          >
            {data.map((entry, i) => (
              <Cell key={i} fill={entry.fill} stroke="#fff" strokeWidth={2} />
            ))}
          </Pie>
          <Tooltip content={<DonutTooltip total={total} unit="assessments" />} />
          <text
            x="50%"
            y="50%"
            textAnchor="middle"
            dominantBaseline="central"
            fill="#0f172a"
            fontSize={20}
            fontWeight={700}
          >
            {total}
          </text>
          <text
            x="50%"
            y="50%"
            dy={18}
            textAnchor="middle"
            dominantBaseline="central"
            fill="#475569"
            fontSize={11}
            fontWeight={600}
          >
            assessments
          </text>
        </PieChart>
      }
    />
  );
}

export function ComplianceScoreGauge({ score }) {
  if (score == null) return <ChartEmpty message="Scores appear after departments lock submissions" />;
  const data = [{ name: "Score", value: score, fill: score >= 75 ? COLORS.emerald : score >= 50 ? COLORS.amber : COLORS.rose }];
  const rating = score >= 75 ? "Strong compliance" : score >= 50 ? "Partial compliance" : "Needs attention";
  return (
    <ResponsiveContainer width="100%" height={260}>
      <RadialBarChart cx="50%" cy="50%" innerRadius="52%" outerRadius="88%" barSize={18} data={data} startAngle={180} endAngle={0}>
        <RadialBar background={{ fill: "#e2e8f0" }} dataKey="value" cornerRadius={10} />
        <Tooltip
          content={({ active }) => {
            if (!active) return null;
            return (
              <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 shadow-lg text-xs max-w-[220px]">
                <p className="font-semibold text-slate-900">Overall compliance score</p>
                <p className="text-slate-700 mt-1">
                  <span className="font-bold text-lg">{score}%</span>
                  <span className="text-slate-500"> · {rating}</span>
                </p>
                <p className="text-slate-500 mt-1.5 leading-snug">
                  Average score across all locked department submissions in this cycle.
                </p>
              </div>
            );
          }}
        />
        <text x="50%" y="46%" textAnchor="middle" dominantBaseline="middle" className="fill-slate-900 text-3xl font-bold" style={{ fontFamily: "Sora, sans-serif" }}>
          {score}%
        </text>
        <text x="50%" y="46%" dy={22} textAnchor="middle" dominantBaseline="middle" className="fill-slate-600 text-xs font-semibold">
          Overall compliance
        </text>
        <text x="50%" y="46%" dy={38} textAnchor="middle" dominantBaseline="middle" className="fill-slate-500 text-[10px]">
          {rating}
        </text>
      </RadialBarChart>
    </ResponsiveContainer>
  );
}

export function DepartmentScoresBar({ rows, height = 280 }) {
  const data = useMemo(() => {
    const filtered = (rows || []).filter((r) => r.score != null);
    const angled = filtered.length > 3;
    return filtered.map((r) => {
      const fullName = r.department_name || r.department_code;
      return {
        name: angled ? abbreviateLabel(fullName, 18) : fullName,
        fullName,
        score: r.score,
      };
    });
  }, [rows]);
  if (!data.length) return <ChartEmpty message="No department scores yet" />;

  const angled = data.length > 3;
  const chartHeight = departmentChartHeight(data.length, height);

  return (
    <LabeledChart height={chartHeight}>
      <BarChart data={data} margin={chartMargin({ angledX: angled, top: 32 })}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis
          dataKey="name"
          tick={makeCategoryTick(data, { angled })}
          axisLine={false}
          tickLine={false}
          interval={0}
          height={angled ? 72 : 36}
          label={xAxisLabel("Department", angled ? 6 : 0)}
        />
        <YAxis
          domain={[0, 100]}
          tick={TICK_STYLE}
          tickFormatter={percentTick}
          axisLine={false}
          tickLine={false}
          width={44}
          label={yAxisLabel("Compliance score")}
        />
        <Tooltip content={({ active, payload }) => {
          if (!active || !payload?.length) return null;
          const p = payload[0]?.payload;
          return (
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-lg text-xs">
              <p className="font-semibold text-slate-800">{p?.fullName}</p>
              <p className="text-slate-600">Score: <span className="font-semibold">{p?.score}%</span></p>
            </div>
          );
        }}
        />
        <Bar dataKey="score" name="Compliance" radius={[6, 6, 0, 0]} maxBarSize={40}>
          <LabelList dataKey="score" content={barValueLabel} formatter={percentBarLabel} />
          {data.map((entry) => (
            <Cell
              key={entry.fullName}
              fill={entry.score >= 75 ? COLORS.emerald : entry.score >= 50 ? COLORS.amber : COLORS.rose}
            />
          ))}
        </Bar>
      </BarChart>
    </LabeledChart>
  );
}

export function ComplianceTrendChart({ trendByYear }) {
  const data = useMemo(
    () => (trendByYear || []).map((t) => ({
      name: String(t.year),
      "Avg Score": t.average_score ?? 0,
      Completion: t.completion_pct ?? 0,
    })),
    [trendByYear],
  );
  if (!data.length) return <ChartEmpty message="Run assessments in multiple cycles to see trends" />;

  return (
    <LabeledChart height={300} legend={<ChartLegend items={LEGEND_ITEMS.complianceTrend} />}>
      <LineChart data={data} margin={chartMargin({ extraBottom: 4 })}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis
          dataKey="name"
          tick={TICK_STYLE}
          axisLine={false}
          tickLine={false}
          label={xAxisLabel("Assessment year")}
        />
        <YAxis
          domain={[0, 100]}
          tick={TICK_STYLE}
          tickFormatter={percentTick}
          axisLine={false}
          tickLine={false}
          width={44}
          label={yAxisLabel("Percentage")}
        />
        <Tooltip content={<ChartTooltip unit="%" />} />
        <Line type="monotone" dataKey="Avg Score" stroke={COLORS.indigo} strokeWidth={2.5} dot={{ r: 4, fill: COLORS.indigo }} />
        <Line type="monotone" dataKey="Completion" stroke={COLORS.teal} strokeWidth={2.5} dot={{ r: 4, fill: COLORS.teal }} />
      </LineChart>
    </LabeledChart>
  );
}

export function SectionScoresBar({ highRisk = [], lowScoring = [] }) {
  const data = useMemo(() => {
    const seen = new Set();
    const items = [];
    [...highRisk, ...lowScoring].forEach((s) => {
      if (!s?.section_id || seen.has(s.section_id)) return;
      seen.add(s.section_id);
      items.push({
        name: truncateLabel(s.title || s.section_id, 18),
        fullTitle: s.title || s.section_id,
        score: s.average_score ?? 0,
        risk: (highRisk || []).some((h) => h.section_id === s.section_id) ? "high" : "low",
      });
    });
    return items.sort((a, b) => a.score - b.score);
  }, [highRisk, lowScoring]);

  if (!data.length) return <ChartEmpty message="All sections scoring well" />;

  const yWidth = Math.min(140, Math.max(96, ...data.map((d) => String(d.name).length * 6.5)));

  return (
    <ResponsiveContainer width="100%" height={Math.max(280, data.length * 36 + 72)}>
      <BarChart layout="vertical" data={data} margin={{ top: 16, right: 56, left: 12, bottom: 56 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
        <XAxis
          type="number"
          domain={[0, 100]}
          tick={TICK_STYLE}
          axisLine={false}
          tickLine={false}
          label={xAxisLabel("Average score (%)")}
        />
        <YAxis
          type="category"
          dataKey="name"
          tick={makeVerticalCategoryTick(data, { nameKey: "fullTitle", displayKey: "name" })}
          width={yWidth}
          axisLine={false}
          tickLine={false}
        />
        <Tooltip content={({ active, payload }) => {
          if (!active || !payload?.length) return null;
          const p = payload[0]?.payload;
          return (
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-lg text-xs">
              <p className="font-semibold text-slate-800">{p?.fullTitle}</p>
              <p className="text-slate-600">Avg: <span className="font-semibold">{p?.score}%</span></p>
            </div>
          );
        }}
        />
        <Bar dataKey="score" name="Avg Score" radius={[0, 6, 6, 0]} maxBarSize={18}>
          <LabelList
            dataKey="score"
            position="right"
            offset={8}
            formatter={percentBarLabel}
            style={VALUE_LABEL_STYLE}
          />
          {data.map((entry) => (
            <Cell key={entry.name} fill={entry.risk === "high" ? COLORS.rose : COLORS.amber} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export function GapByDepartmentChart({ byDepartment }) {
  const data = useMemo(() => {
    const rows = byDepartment || [];
    const angled = rows.length > 3;
    return rows.map((r) => ({
      name: angled ? abbreviateLabel(r.department_name, 18) : r.department_name,
      fullName: r.department_name,
      Critical: r.critical_gaps ?? 0,
      "High Priority": r.high_priority_gaps ?? 0,
      Other: Math.max(0, (r.total_gaps ?? 0) - (r.critical_gaps ?? 0) - (r.high_priority_gaps ?? 0)),
    }));
  }, [byDepartment]);
  if (!data.length) return <ChartEmpty message="No gap data for this cycle" />;

  const angled = data.length > 3;
  const chartHeight = departmentChartHeight(data.length, 320);

  return (
    <LabeledChart height={chartHeight} legend={<ChartLegend items={LEGEND_ITEMS.gapStack} />}>
      <BarChart data={data} margin={chartMargin({ angledX: angled, extraBottom: 4 })}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis
          dataKey="name"
          tick={makeCategoryTick(data, { angled })}
          axisLine={false}
          tickLine={false}
          interval={0}
          height={angled ? 72 : 36}
          label={xAxisLabel("Department", angled ? 6 : 0)}
        />
        <YAxis
          tick={TICK_STYLE}
          axisLine={false}
          tickLine={false}
          width={40}
          allowDecimals={false}
          label={yAxisLabel("Gap count")}
        />
        <Tooltip content={({ active, payload }) => {
          if (!active || !payload?.length) return null;
          const p = payload[0]?.payload;
          const total = payload.reduce((sum, item) => sum + (Number(item.value) || 0), 0);
          return (
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-lg text-xs space-y-0.5">
              <p className="font-semibold text-slate-800">{p?.fullName}</p>
              {payload.map((item) => (
                <p key={item.name} className="text-slate-600">
                  <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: item.color || item.fill }} />
                  {item.name}: <span className="font-semibold">{item.value}</span>
                </p>
              ))}
              <p className="text-slate-500 pt-0.5 border-t border-slate-100">Total: <span className="font-semibold text-slate-700">{total}</span></p>
            </div>
          );
        }}
        />
        <Bar dataKey="Critical" stackId="gaps" fill={COLORS.rose} radius={[0, 0, 0, 0]} />
        <Bar dataKey="High Priority" stackId="gaps" fill={COLORS.amber} />
        <Bar dataKey="Other" stackId="gaps" fill={COLORS.slate} radius={[6, 6, 0, 0]} />
      </BarChart>
    </LabeledChart>
  );
}

export function GapTrendChart({ trendByYear }) {
  const data = useMemo(
    () => (trendByYear || []).map((t) => ({
      name: String(t.year),
      "Total Gaps": t.total_gaps ?? 0,
      "Depts Submitted": t.submitted_departments ?? 0,
    })),
    [trendByYear],
  );
  if (!data.length) return <ChartEmpty message="No historical gap data" />;

  return (
    <LabeledChart height={300} legend={<ChartLegend items={LEGEND_ITEMS.gapTrend} />}>
      <AreaChart data={data} margin={chartMargin({ dualY: true, extraBottom: 4 })}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis
          dataKey="name"
          tick={TICK_STYLE}
          axisLine={false}
          tickLine={false}
          label={xAxisLabel("Assessment year")}
        />
        <YAxis
          yAxisId="left"
          tick={TICK_STYLE}
          axisLine={false}
          tickLine={false}
          width={40}
          allowDecimals={false}
          label={yAxisLabel("Total gaps")}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          tick={TICK_STYLE}
          axisLine={false}
          tickLine={false}
          width={40}
          allowDecimals={false}
          label={yAxisLabelRight("Departments submitted")}
        />
        <Tooltip content={<ChartTooltip />} />
        <Area yAxisId="left" type="monotone" dataKey="Total Gaps" stroke={COLORS.rose} fill={COLORS.rose} fillOpacity={0.15} strokeWidth={2} />
        <Line yAxisId="right" type="monotone" dataKey="Depts Submitted" stroke={COLORS.indigo} strokeWidth={2} dot={{ r: 4 }} />
      </AreaChart>
    </LabeledChart>
  );
}

export function YearComparisonChart({ yearComparison }) {
  const data = useMemo(
    () => (yearComparison || []).map((r) => ({
      name: String(r.year),
      "Avg Score": r.average_score ?? 0,
      "Total Gaps": r.total_gaps ?? 0,
      Completion: r.completion_pct ?? 0,
    })),
    [yearComparison],
  );
  if (!data.length) return <ChartEmpty message="No historical cycles yet" />;

  return (
    <LabeledChart height={320} legend={<ChartLegend items={LEGEND_ITEMS.yearComparison} />}>
      <BarChart data={data} margin={chartMargin({ dualY: true, extraBottom: 4 })}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis
          dataKey="name"
          tick={TICK_STYLE}
          axisLine={false}
          tickLine={false}
          label={xAxisLabel("Assessment year")}
        />
        <YAxis
          yAxisId="pct"
          domain={[0, 100]}
          tick={TICK_STYLE}
          tickFormatter={percentTick}
          axisLine={false}
          tickLine={false}
          width={44}
          label={yAxisLabel("Score")}
        />
        <YAxis
          yAxisId="count"
          orientation="right"
          tick={TICK_STYLE}
          axisLine={false}
          tickLine={false}
          width={40}
          allowDecimals={false}
          label={yAxisLabelRight("Total gaps")}
        />
        <Tooltip content={({ active, payload, label }) => {
          if (!active || !payload?.length) return null;
          return (
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-lg text-xs">
              <p className="font-semibold text-slate-800 mb-1">{label}</p>
              {payload.map((p) => {
                const isPct = p.dataKey === "Avg Score" || p.dataKey === "Completion";
                return (
                  <p key={p.dataKey} className="text-slate-600">
                    <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: p.color || p.fill }} />
                    {p.name}: <span className="font-semibold text-slate-900">{p.value}{isPct ? "%" : ""}</span>
                  </p>
                );
              })}
            </div>
          );
        }}
        />
        <Bar yAxisId="pct" dataKey="Avg Score" fill={COLORS.indigo} radius={[4, 4, 0, 0]} maxBarSize={24} name="Avg Score (%)" />
        <Bar yAxisId="pct" dataKey="Completion" fill={COLORS.teal} radius={[4, 4, 0, 0]} maxBarSize={24} name="Completion (%)" />
        <Bar yAxisId="count" dataKey="Total Gaps" fill={COLORS.rose} radius={[4, 4, 0, 0]} maxBarSize={24} />
      </BarChart>
    </LabeledChart>
  );
}

export function ScoreDeltaChart({ scoreChanges }) {
  const data = useMemo(
    () => (scoreChanges || [])
      .filter((r) => r.score_delta != null)
      .slice(0, 12)
      .map((r) => ({
        name: abbreviateLabel(r.department_name, 18),
        fullName: r.department_name,
        delta: r.score_delta,
      })),
    [scoreChanges],
  );
  if (!data.length) return <ChartEmpty message="Need 2+ cycles with submissions to compare" />;

  const chartHeight = departmentChartHeight(data.length, 300);

  return (
    <LabeledChart height={chartHeight}>
      <BarChart data={data} margin={chartMargin({ angledX: true, top: 32 })}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis
          dataKey="name"
          tick={makeCategoryTick(data, { angled: true })}
          height={72}
          interval={0}
          axisLine={false}
          tickLine={false}
          label={xAxisLabel("Department", 6)}
        />
        <YAxis
          tick={TICK_STYLE}
          axisLine={false}
          tickLine={false}
          width={40}
          label={yAxisLabel("Score change (pts)")}
        />
        <Tooltip content={({ active, payload }) => {
          if (!active || !payload?.length) return null;
          const p = payload[0]?.payload;
          return (
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-lg text-xs">
              <p className="font-semibold text-slate-800">{p?.fullName}</p>
              <p className="text-slate-600">Score change: <span className="font-semibold">{p?.delta > 0 ? "+" : ""}{p?.delta} pts</span></p>
            </div>
          );
        }}
        />
        <Bar dataKey="delta" name="Score Δ" radius={[6, 6, 0, 0]} maxBarSize={36}>
          <LabelList
            dataKey="delta"
            position="top"
            formatter={(v) => signedLabel(v, " pts")}
            style={VALUE_LABEL_STYLE}
          />
          {data.map((entry) => (
            <Cell key={entry.name} fill={entry.delta >= 0 ? COLORS.emerald : COLORS.rose} />
          ))}
        </Bar>
      </BarChart>
    </LabeledChart>
  );
}

export function RecommendationsByDepartmentChart({ byDepartment }) {
  const data = useMemo(
    () => (byDepartment || [])
      .filter((r) => (r.count ?? 0) > 0)
      .map((r) => ({
        name: abbreviateLabel(r.department_name, 18),
        fullName: r.department_name,
        count: r.count,
      })),
    [byDepartment],
  );
  if (!data.length) return <ChartEmpty message="No recommendations yet" />;

  const chartHeight = departmentChartHeight(data.length, 280);

  return (
    <LabeledChart height={chartHeight}>
      <BarChart data={data} margin={chartMargin({ angledX: true, top: 32 })}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis
          dataKey="name"
          tick={makeCategoryTick(data, { angled: true })}
          height={72}
          interval={0}
          axisLine={false}
          tickLine={false}
          label={xAxisLabel("Department", 6)}
        />
        <YAxis
          tick={TICK_STYLE}
          axisLine={false}
          tickLine={false}
          width={40}
          allowDecimals={false}
          label={yAxisLabel("Recommendations")}
        />
        <Tooltip content={({ active, payload }) => {
          if (!active || !payload?.length) return null;
          const p = payload[0]?.payload;
          return (
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-lg text-xs">
              <p className="font-semibold text-slate-800">{p?.fullName}</p>
              <p className="text-slate-600">Comments: <span className="font-semibold">{p?.count}</span></p>
            </div>
          );
        }}
        />
        <Bar dataKey="count" name="Recommendations" fill={COLORS.amber} radius={[6, 6, 0, 0]} maxBarSize={40}>
          <LabelList dataKey="count" position="top" style={VALUE_LABEL_STYLE} />
        </Bar>
      </BarChart>
    </LabeledChart>
  );
}

export function GapDeltaChart({ gapChanges }) {
  const data = useMemo(
    () => (gapChanges || [])
      .filter((r) => r.gap_delta != null)
      .slice(0, 12)
      .map((r) => ({
        name: abbreviateLabel(r.department_name, 18),
        fullName: r.department_name,
        delta: r.gap_delta,
      })),
    [gapChanges],
  );
  if (!data.length) return <ChartEmpty message="Need 2+ cycles with submissions to compare" />;

  const chartHeight = departmentChartHeight(data.length, 300);

  return (
    <LabeledChart height={chartHeight}>
      <BarChart data={data} margin={chartMargin({ angledX: true, top: 32 })}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis
          dataKey="name"
          tick={makeCategoryTick(data, { angled: true })}
          height={72}
          interval={0}
          axisLine={false}
          tickLine={false}
          label={xAxisLabel("Department", 6)}
        />
        <YAxis
          tick={TICK_STYLE}
          axisLine={false}
          tickLine={false}
          width={40}
          label={yAxisLabel("Gap change (count)")}
        />
        <Tooltip content={({ active, payload }) => {
          if (!active || !payload?.length) return null;
          const p = payload[0]?.payload;
          return (
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-lg text-xs">
              <p className="font-semibold text-slate-800">{p?.fullName}</p>
              <p className="text-slate-600">Gap change: <span className="font-semibold">{p?.delta > 0 ? "+" : ""}{p?.delta}</span></p>
            </div>
          );
        }}
        />
        <Bar dataKey="delta" name="Gap Δ" radius={[6, 6, 0, 0]} maxBarSize={36}>
          <LabelList
            dataKey="delta"
            position="top"
            formatter={(v) => signedLabel(v)}
            style={VALUE_LABEL_STYLE}
          />
          {data.map((entry) => (
            <Cell key={entry.fullName} fill={entry.delta <= 0 ? COLORS.emerald : COLORS.rose} />
          ))}
        </Bar>
      </BarChart>
    </LabeledChart>
  );
}

export function RecommendationsByCycleChart({ byCycle }) {
  const data = useMemo(
    () => (byCycle || []).map((r) => ({
      name: String(r.year),
      count: r.total_recommendations ?? 0,
      cycle: r.name,
    })),
    [byCycle],
  );
  if (!data.length) return <ChartEmpty message="No cycle data yet" />;

  return (
    <LabeledChart height={280}>
      <BarChart data={data} margin={chartMargin({ top: 32 })}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis
          dataKey="name"
          tick={TICK_STYLE}
          axisLine={false}
          tickLine={false}
          label={xAxisLabel("Assessment year")}
        />
        <YAxis
          tick={TICK_STYLE}
          axisLine={false}
          tickLine={false}
          width={40}
          allowDecimals={false}
          label={yAxisLabel("Recommendations")}
        />
        <Tooltip content={({ active, payload }) => {
          if (!active || !payload?.length) return null;
          const p = payload[0]?.payload;
          return (
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-lg text-xs">
              <p className="font-semibold text-slate-800">{p?.cycle} ({p?.name})</p>
              <p className="text-slate-600">Recommendations: <span className="font-semibold">{p?.count}</span></p>
            </div>
          );
        }}
        />
        <Bar dataKey="count" name="Recommendations" fill={COLORS.indigo} radius={[6, 6, 0, 0]} maxBarSize={40}>
          <LabelList dataKey="count" position="top" style={VALUE_LABEL_STYLE} />
        </Bar>
      </BarChart>
    </LabeledChart>
  );
}

export function EvidenceByDepartmentChart({ byDepartment }) {
  const data = useMemo(() => {
    const rows = byDepartment || [];
    const angled = rows.length > 3;
    return rows.map((r) => ({
      name: angled ? abbreviateLabel(r.department_name, 18) : r.department_name,
      fullName: r.department_name,
      Uploaded: r.uploaded ?? 0,
      Missing: r.missing ?? 0,
    }));
  }, [byDepartment]);
  if (!data.length) return <ChartEmpty message="No evidence data for this cycle" />;

  const angled = data.length > 3;
  const chartHeight = departmentChartHeight(data.length, 320);

  return (
    <LabeledChart height={chartHeight} legend={<ChartLegend items={LEGEND_ITEMS.evidenceStack} />}>
      <BarChart data={data} margin={chartMargin({ angledX: angled, extraBottom: 4 })}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis
          dataKey="name"
          tick={makeCategoryTick(data, { angled })}
          axisLine={false}
          tickLine={false}
          interval={0}
          height={angled ? 72 : 36}
          label={xAxisLabel("Department", angled ? 6 : 0)}
        />
        <YAxis
          tick={TICK_STYLE}
          axisLine={false}
          tickLine={false}
          width={40}
          allowDecimals={false}
          label={yAxisLabel("Evidence files")}
        />
        <Tooltip content={({ active, payload }) => {
          if (!active || !payload?.length) return null;
          const p = payload[0]?.payload;
          return (
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-lg text-xs space-y-0.5">
              <p className="font-semibold text-slate-800">{p?.fullName}</p>
              {payload.map((item) => (
                <p key={item.name} className="text-slate-600">
                  <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: item.color || item.fill }} />
                  {item.name}: <span className="font-semibold">{item.value}</span>
                </p>
              ))}
            </div>
          );
        }}
        />
        <Bar dataKey="Uploaded" stackId="evidence" fill={COLORS.emerald} />
        <Bar dataKey="Missing" stackId="evidence" fill={COLORS.amber} radius={[6, 6, 0, 0]} />
      </BarChart>
    </LabeledChart>
  );
}

export { ChartCard };
